// Exploratory-testing (ET) logic for the testing views. Dependency-free and
// DOM-free so it can be unit-tested under bun; the views stay thin renderers.

/** An item last tested longer ago than this is stale. */
export const STALE_AFTER_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The outcomes, in the order the console offers them, with their badge style. */
export const OUTCOMES = [
    { value: 'pass', label: 'Pass', badge: 'text-bg-success', button: 'btn-outline-success' },
    { value: 'fail', label: 'Fail', badge: 'text-bg-danger', button: 'btn-outline-danger' },
    { value: 'blocked', label: 'Blocked', badge: 'text-bg-warning', button: 'btn-outline-warning' },
    {
        value: 'skipped',
        label: 'Skipped',
        badge: 'text-bg-secondary',
        button: 'btn-outline-secondary',
    },
];

/**
 * @param {string} value
 * @returns {{value: string, label: string, badge: string, button: string}|undefined}
 */
export function outcomeOf(value) {
    return OUTCOMES.find((outcome) => outcome.value === value);
}

/** The ways a plan's items can be narrowed, in the order the filter bar offers them. */
export const ITEM_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'never', label: 'Never tested' },
    { value: 'stale', label: `Not tested in ${STALE_AFTER_DAYS} days` },
    { value: 'failing', label: 'Failing or blocked' },
    { value: 'open-issues', label: 'Open issues' },
];

/**
 * How fresh an item's testing is.
 * @param {{lastResult?: {createdAt: string}}} item
 * @param {number} now epoch millis
 * @returns {'never'|'stale'|'recent'}
 */
export function stalenessOf(item, now) {
    if (!item.lastResult) return 'never';
    const testedAt = Date.parse(item.lastResult.createdAt);
    return now - testedAt > STALE_AFTER_DAYS * DAY_MS ? 'stale' : 'recent';
}

/**
 * Whether an item passes a filter. "stale" includes never-tested items: both
 * are what a tester should pick up next.
 * @param {{lastResult?: {outcome: string, createdAt: string}, issues: Array<{state: string}>}} item
 * @param {string} filter one of ITEM_FILTERS' values
 * @param {number} now epoch millis
 */
export function itemMatches(item, filter, now) {
    switch (filter) {
        case 'never':
            return !item.lastResult;
        case 'stale':
            return stalenessOf(item, now) !== 'recent';
        case 'failing':
            return item.lastResult?.outcome === 'fail' || item.lastResult?.outcome === 'blocked';
        case 'open-issues':
            return item.issues.some((issue) => issue.state === 'open');
        default:
            return true;
    }
}

/**
 * Order items for picking what to test: never tested first, then the
 * longest-untested, keeping plan order among equals. Returns a new array.
 * @template {{lastResult?: {createdAt: string}, position: number}} T
 * @param {T[]} items
 * @returns {T[]}
 */
export function sortStalestFirst(items) {
    const testedAt = (item) =>
        item.lastResult ? Date.parse(item.lastResult.createdAt) : Number.NEGATIVE_INFINITY;
    return [...items].sort((a, b) => testedAt(a) - testedAt(b) || a.position - b.position);
}

/**
 * Whether a claim is still in force.
 * @param {{expiresAt: string}|null|undefined} claim
 * @param {number} now epoch millis
 */
export function isClaimActive(claim, now) {
    return !!claim && Date.parse(claim.expiresAt) > now;
}

/**
 * A short "how long ago" for a timestamp.
 * @param {string} iso
 * @param {number} now epoch millis
 */
export function timeAgo(iso, now) {
    const minutes = Math.floor((now - Date.parse(iso)) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 60) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
}

/**
 * Parse an issue reference typed by a tester: "dice-driven/repo#12", a GitHub
 * issue URL, or "#12" / "12" meaning the plan's default repo.
 * @param {string} text
 * @param {string} defaultRepo
 * @returns {{repo: string, number: number}|null}
 */
export function parseIssueRef(text, defaultRepo) {
    const value = text.trim();
    const url = value.match(/^https:\/\/github\.com\/([\w.-]+\/[\w.-]+)\/issues\/(\d+)\/?$/);
    if (url) return { repo: url[1], number: Number(url[2]) };
    const full = value.match(/^([\w.-]+\/[\w.-]+)#(\d+)$/);
    if (full) return { repo: full[1], number: Number(full[2]) };
    const bare = value.match(/^#?(\d+)$/);
    if (bare && defaultRepo) return { repo: defaultRepo, number: Number(bare[1]) };
    return null;
}

/**
 * Split pasted text into item lines: one item per non-blank line, with list
 * markers ("- ", "* ", "1. ") stripped, so a list pasted from a doc becomes items.
 * @param {string} text
 * @returns {string[]}
 */
export function splitItemLines(text) {
    return text
        .split(/\r?\n/)
        .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
        .filter((line) => line !== '');
}

/**
 * Move one id up or down a list, for reordering. Returns a new array, or null
 * when the move would leave the list.
 * @param {string[]} ids
 * @param {string} id
 * @param {-1|1} delta
 * @returns {string[]|null}
 */
export function moveId(ids, id, delta) {
    const from = ids.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return null;
    const moved = [...ids];
    [moved[from], moved[to]] = [moved[to], moved[from]];
    return moved;
}
